import { useCallback, useEffect, useState } from 'react'
import { adminRequest } from './EshopCategories'

/** Správa používateľov administrácie – vlastné prihlasovacie meno a heslo pre každého zamestnanca (meno sa použije napr. v poli „Vystavil“ na dokladoch). */

type AdminUser = { id: string; username: string; name: string; active: boolean; createdAt: string; lastLoginAt: string | null }

const MIN_PASSWORD = 10
const dateTime = (value: string | null) => value ? new Date(value).toLocaleString('sk-SK') : 'ešte sa neprihlásil'

export default function UsersAdmin() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)

  const [newUsername, setNewUsername] = useState('')
  const [newName, setNewName] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [adding, setAdding] = useState(false)

  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({})
  const [passwordRowId, setPasswordRowId] = useState<string | null>(null)
  const [passwordDraft, setPasswordDraft] = useState('')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const data = await adminRequest('/api/admin/users')
      setUsers(Array.isArray(data?.users) ? data.users : [])
    } catch (e) { setError(e instanceof Error ? e.message : 'Používateľov sa nepodarilo načítať.') }
    finally { setLoading(false) }
  }, [])
  useEffect(() => { void load() }, [load])

  async function add(event: React.FormEvent) {
    event.preventDefault()
    setError(''); setMessage('')
    setAdding(true)
    try {
      const data = await adminRequest('/api/admin/users', { method: 'POST', body: JSON.stringify({ username: newUsername.trim(), name: newName.trim(), password: newPassword }) })
      setUsers(current => [...current, data.user].sort((a, b) => a.name.localeCompare(b.name, 'sk')))
      setMessage(`Používateľ „${data.user.name}“ je pridaný.`)
      setNewUsername(''); setNewName(''); setNewPassword('')
    } catch (e) { setError(e instanceof Error ? e.message : 'Pridanie sa nepodarilo.') }
    finally { setAdding(false) }
  }

  async function patch(user: AdminUser, body: Record<string, unknown>, onOk?: () => void) {
    setBusyId(user.id); setError(''); setMessage('')
    try {
      const data = await adminRequest(`/api/admin/users/${encodeURIComponent(user.id)}`, { method: 'PATCH', body: JSON.stringify(body) })
      setUsers(current => current.map(u => u.id === user.id ? data.user : u))
      onOk?.()
    } catch (e) { setError(e instanceof Error ? e.message : 'Uloženie sa nepodarilo.') }
    finally { setBusyId(null) }
  }

  function commitName(user: AdminUser) {
    const draft = nameDrafts[user.id]
    if (draft === undefined) return
    const name = draft.trim()
    setNameDrafts(current => { const next = { ...current }; delete next[user.id]; return next })
    if (!name || name === user.name) return
    void patch(user, { name })
  }

  function commitPassword(user: AdminUser) {
    if (passwordDraft.length < MIN_PASSWORD) { setError(`Heslo musí mať aspoň ${MIN_PASSWORD} znakov.`); return }
    void patch(user, { password: passwordDraft }, () => { setMessage(`${user.name}: heslo je zmenené.`); setPasswordRowId(null); setPasswordDraft('') })
  }

  async function remove(user: AdminUser) {
    if (!window.confirm(`Zmazať používateľa „${user.name}“? Prihlasovacie meno „${user.username}“ sa už nebude dať použiť.`)) return
    setBusyId(user.id); setError(''); setMessage('')
    try {
      await adminRequest(`/api/admin/users/${encodeURIComponent(user.id)}`, { method: 'DELETE' })
      setUsers(current => current.filter(u => u.id !== user.id))
      setMessage(`Používateľ „${user.name}“ je zmazaný.`)
    } catch (e) { setError(e instanceof Error ? e.message : 'Zmazanie sa nepodarilo.') }
    finally { setBusyId(null) }
  }

  return <div className="panel delivery-admin">
    <div className="pa-head">
      <div><h2>Používatelia administrácie</h2><p>Vlastné prihlásenie pre každého zamestnanca – jeho meno sa potom píše napr. do poľa „Vystavil“ na dokladoch. Hlavný prístup (z nastavenia servera) ostáva funkčný aj naďalej ako núdzový.</p></div>
      <div className="pa-head-actions"><button type="button" className="pa-ghost" onClick={() => void load()} disabled={loading}>↻ Obnoviť</button></div>
    </div>

    <form className="dl-add" onSubmit={add}>
      <input placeholder="Meno a priezvisko" value={newName} maxLength={80} onChange={e => setNewName(e.target.value)} aria-label="Meno a priezvisko" required />
      <input placeholder="Prihlasovacie meno" value={newUsername} maxLength={40} pattern="[a-z0-9._-]+" title="Iba malé písmená, číslice, bodka, pomlčka a podčiarnik" onChange={e => setNewUsername(e.target.value)} aria-label="Prihlasovacie meno" required />
      <input placeholder={`Heslo (aspoň ${MIN_PASSWORD} znakov)`} type="password" value={newPassword} minLength={MIN_PASSWORD} onChange={e => setNewPassword(e.target.value)} aria-label="Heslo" required />
      <button className="primary-btn" disabled={adding || newName.trim().length < 1 || newUsername.trim().length < 3 || newPassword.length < MIN_PASSWORD}>＋ Pridať používateľa</button>
    </form>

    {error && <p className="error-message" role="alert">{error}</p>}
    {message && <p className="pa-message" role="status">{message}</p>}

    {loading ? <p className="empty-message">Načítavam používateľov…</p> : !users.length ? <div className="pa-empty"><span>♡</span><strong>Zatiaľ nie je pridaný žiadny používateľ – pridajte prvého vyššie.</strong></div> :
      <div className="pa-table-wrap"><table className="pa-table">
        <thead><tr><th>Meno</th><th>Prihlasovacie meno</th><th>Posledné prihlásenie</th><th>Aktívny</th><th aria-label="Akcie" /></tr></thead>
        <tbody>{users.map(user => {
          const nameDraft = nameDrafts[user.id]
          const busy = busyId === user.id
          return <tr key={user.id} className={user.active ? '' : 'is-inactive'}>
            <td className="pa-name">
              <input aria-label={`Meno pre ${user.username}`} value={nameDraft ?? user.name} disabled={busy}
                onChange={e => setNameDrafts(current => ({ ...current, [user.id]: e.target.value }))}
                onBlur={() => commitName(user)}
                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setNameDrafts(current => { const next = { ...current }; delete next[user.id]; return next }) }} />
            </td>
            <td><code>{user.username}</code></td>
            <td>{dateTime(user.lastLoginAt)}</td>
            <td>
              <button type="button" role="switch" aria-checked={user.active} aria-label={`Aktívny – ${user.name}`} className={`pa-switch ${user.active ? 'is-on' : ''}`} disabled={busy} onClick={() => void patch(user, { active: !user.active })}><span /></button>
            </td>
            <td className="dl-actions">
              {passwordRowId === user.id ? <span className="dl-price-input is-dirty">
                <input type="password" autoFocus placeholder={`Nové heslo (aspoň ${MIN_PASSWORD} znakov)`} value={passwordDraft} onChange={e => setPasswordDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') commitPassword(user); if (e.key === 'Escape') { setPasswordRowId(null); setPasswordDraft('') } }} />
                <button type="button" className="pa-link" onClick={() => commitPassword(user)}>Uložiť</button>
                <button type="button" className="pa-link" onClick={() => { setPasswordRowId(null); setPasswordDraft('') }}>Zrušiť</button>
              </span> : <>
                <button type="button" className="pa-link" onClick={() => { setPasswordRowId(user.id); setPasswordDraft('') }}>Zmeniť heslo</button>
                {' · '}
                <button type="button" className="pa-link dl-remove" onClick={() => void remove(user)}>Zmazať</button>
              </>}
            </td>
          </tr>
        })}</tbody>
      </table></div>}
    <p className="st-note">Meno sa uloží po kliknutí mimo poľa alebo klávesom Enter. Vypnutý používateľ sa už neprihlási (otvorené prihlásenie mu ale platí, kým ho sám neodhlási, najviac 4 hodiny).</p>
  </div>
}
